import axios, { type AxiosRequestConfig, type AxiosResponse, type InternalAxiosRequestConfig } from 'axios';
import { mockInterceptor } from './mockInterceptor';
import { mockDataManager } from './mockData';
import { getAccessToken, getRefreshToken, setTokens, clearTokens } from './tokenStorage';

const http = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  responseType: 'json',
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  }
});

// Add bearer token to requests
http.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const accessToken = getAccessToken();
  // Skip adding token for login and token refresh endpoints
  const skipAuth = config.url?.includes('/api/login') || config.url?.includes('/api/token/refresh');
  
  if (accessToken && !skipAuth && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  
  return config;
}, null);

/** The refresh request gives up after this long, so a hung one cannot hold the lock below forever. */
const REFRESH_TIMEOUT_MS = 30_000;

/** The name of the browser-wide lock every tab takes before it refreshes. */
const REFRESH_LOCK_NAME = 'tabasco-token-refresh';

/**
 * Leaving the page for the login page. It is an object so a test can watch
 * the navigation: jsdom cannot follow an assignment to `location.href`.
 */
export const browserNavigation = {
  assign(url: string): void {
    window.location.href = url;
  },
};

/**
 * The login page, with `?redirect=` set to the page the person is on, so they
 * come back to it after signing in. The router guard's `loginFor` does the same
 * for navigations. No redirect is added for the home page, or for an account
 * page such as sign-out, which would sign the person straight out again.
 */
export function loginUrlWithRedirect(): string {
  const { pathname, search, hash } = window.location;
  if (pathname === '/' || pathname.startsWith('/account/')) {
    return '/account/login';
  }
  return `/account/login?redirect=${encodeURIComponent(`${pathname}${search}${hash}`)}`;
}

/**
 * Runs `work` while this tab holds the browser-wide refresh lock, so no two tabs
 * refresh at once. A browser without `navigator.locks` runs it straight away.
 */
function withRefreshLock<T>(work: () => Promise<T>): Promise<T> {
  const locks = typeof navigator === 'undefined' ? undefined : navigator.locks;
  if (!locks) return work();
  return locks.request(REFRESH_LOCK_NAME, work) as Promise<T>;
}

/**
 * The access token another tab saved while this tab's refresh was on its way.
 * Storage holds a different refresh token by then. If it holds none, that tab
 * signed out.
 */
function accessTokenFromAnotherTab(): string {
  const accessToken = getAccessToken();
  if (!getRefreshToken() || !accessToken) {
    throw new Error('Signed out in another tab during the refresh');
  }
  return accessToken;
}

/**
 * Exchanges the refresh token for new tokens, once per tab and once across tabs.
 *
 * The backend rotates the refresh token on every refresh. A token that was
 * already replaced counts as reuse, and the backend then revokes every token
 * the user has. So two refreshes with the same token must never be sent: not
 * by two requests in one tab, and not by two tabs that got a 401 at the same
 * moment.
 *
 * - Inside one tab, every 401 shares one promise.
 * - Across tabs, the refresh runs inside a browser-wide lock. The tab reads the
 *   refresh token again once it holds the lock. If it is no longer the one the
 *   tab held when its request failed, another tab has refreshed already, and
 *   this tab uses the access token that tab saved.
 * - The new tokens are saved, and a failed refresh clears the tokens, only if
 *   storage still holds the refresh token that was sent. Otherwise another tab
 *   signed out or signed in during the request, and this tab must not undo
 *   that. After a sign-in, this tab carries on with the access token that
 *   sign-in saved; after a sign-out, it fails.
 *
 * `refreshedHere` says whether the access token came from a refresh this tab
 * sent. When it is false, the token is one another tab saved.
 */
interface AcquiredAccessToken {
  accessToken: string;
  refreshedHere: boolean;
}

let refreshAccessTokenWork: Promise<AcquiredAccessToken> | null = null;

function fromAnotherTab(accessToken: string): AcquiredAccessToken {
  return { accessToken, refreshedHere: false };
}

function acquireRefreshedAccessToken(heldRefreshToken: string | null): Promise<AcquiredAccessToken> {
  if (!refreshAccessTokenWork) {
    refreshAccessTokenWork = withRefreshLock(async () => {
      const refreshToken = getRefreshToken();
      if (!refreshToken) {
        clearTokens();
        throw new Error('No refresh token available');
      }
      const storedAccessToken = getAccessToken();
      if (refreshToken !== heldRefreshToken && storedAccessToken) {
        return fromAnotherTab(storedAccessToken);
      }
      let response: AxiosResponse;
      try {
        response = await axios.post(`${import.meta.env.VITE_API_URL}/api/token/refresh`, {}, {
          headers: {
            'Authorization': `Bearer ${refreshToken}`,
            'Accept': 'application/json'
          },
          timeout: REFRESH_TIMEOUT_MS,
        });
      } catch (refreshError) {
        if (getRefreshToken() !== refreshToken) return fromAnotherTab(accessTokenFromAnotherTab());
        clearTokens();
        throw refreshError;
      }
      if (getRefreshToken() !== refreshToken) return fromAnotherTab(accessTokenFromAnotherTab());
      const { access_token, refresh_token } = response.data;
      setTokens(access_token, refresh_token);
      return { accessToken: access_token as string, refreshedHere: true };
    }).finally(() => {
      refreshAccessTokenWork = null;
    });
  }
  return refreshAccessTokenWork;
}

/**
 * A request that got a 401 is sent again at most this many times. The second
 * time only follows a token another tab saved. That token can have expired
 * before this tab used it: Safari pauses a background tab, and a paused tab can
 * save tokens it was given more than 15 minutes earlier (TABASCO-1V9). The
 * second time, this tab sends a refresh of its own.
 */
const MAX_TOKEN_RETRIES = 2;

type TokenRetriedRequest = InternalAxiosRequestConfig & {
  /** How many times this request has been sent again after a 401. */
  _tokenRetries?: number;
  /** Whether it was last sent with a token from this tab's own refresh. A 401 after that is not an expired token. */
  _retriedWithOwnRefresh?: boolean;
};

http.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config as TokenRetriedRequest;
    const tokenRetries = originalRequest._tokenRetries ?? 0;

    if (error.response?.status === 401 && !originalRequest._retriedWithOwnRefresh && tokenRetries < MAX_TOKEN_RETRIES) {
      originalRequest._tokenRetries = tokenRetries + 1;
      const heldRefreshToken = getRefreshToken();

      try {
        const { accessToken, refreshedHere } = await acquireRefreshedAccessToken(heldRefreshToken);
        originalRequest._retriedWithOwnRefresh = refreshedHere;
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        }
        return http(originalRequest);
      } catch (refreshError) {
        // Only redirect if not already on a public route to prevent loops
        if (typeof window !== 'undefined') {
          const currentPath = window.location.pathname;
          const isPublicRoute = currentPath.includes('/account/login') || 
                               currentPath.includes('/account/register') ||
                               currentPath.includes('/account/forgot-password') ||
                               currentPath.includes('/account/reset-password') ||
                               currentPath.includes('/account/verify-email');
          if (!isPublicRoute) {
            browserNavigation.assign(loginUrlWithRedirect());
          }
        }
        return Promise.reject(refreshError);
      }
    }

    // A request that never received a response (server unreachable, CORS, DNS,
    // dropped connection) surfaces as axios's terse "Network Error". Swap in an
    // actionable message so error toasts read sensibly; `error.code`
    // ('ERR_NETWORK') stays intact for programmatic checks, and deliberate
    // request cancellations are left alone.
    if (axios.isAxiosError(error) && !error.response && error.code !== 'ERR_CANCELED') {
      error.message = "Can't reach the server — check your connection";
    }

    return Promise.reject(error);
  }
);

// Setup mock interceptors
mockInterceptor.setupInterceptors(http);

// Initialize mock data if in replay mode
const initializeMockData = async (): Promise<void> => {
  if (mockInterceptor.getMode() === 'replay') {
    console.log('🎭 [Mock System] Initializing mock data from public/mockData.json...');
    await mockDataManager.initializeForReplay();
  }
};

// Initialize mock data immediately
const mockInitPromise = initializeMockData();

// Mock-aware HTTP wrapper
class MockAwareHTTP {
  private axiosInstance = http;

  async request<T = unknown>(config: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    const mode = mockInterceptor.getMode();
    
    if (mode === 'replay') {
      // Wait for mock data to be loaded
      await mockInitPromise;
      
      const mockResponse = mockDataManager.getResponse(config);
      if (mockResponse) {
        // Enhanced logging with better formatting
        const method = config.method?.toUpperCase() || 'GET';
        const url = config.url || '';
        const status = mockResponse.status;
        const timestamp = new Date().toLocaleTimeString();
        
        // Get summary of response data
        let dataSummary = '';
        if (mockResponse.data) {
          if (Array.isArray(mockResponse.data)) {
            dataSummary = `array[${mockResponse.data.length}]`;
          } else if (typeof mockResponse.data === 'object') {
            const keys = Object.keys(mockResponse.data);
            dataSummary = `object{${keys.slice(0, 3).join(', ')}${keys.length > 3 ? '...' : ''}}`;
          } else {
            dataSummary = typeof mockResponse.data;
          }
        }
        
        console.log(`%c🎭 MOCK RESPONSE RETURNED`, 'color: #2563eb; font-weight: bold; font-size: 12px;');
        console.log(`%c   ${method} ${url}`, 'color: #059669; font-weight: bold;');
        console.log(`%c   Status: ${status} ${mockResponse.statusText}`, 'color: #7c3aed;');
        console.log(`%c   Data: ${dataSummary}`, 'color: #dc2626;');
        console.log(`%c   Time: ${timestamp}`, 'color: #6b7280;');
        
        // Create a mock response that looks like a real axios response
        const response: AxiosResponse<T> = {
          data: mockResponse.data as T,
          status: mockResponse.status,
          statusText: mockResponse.statusText,
          headers: mockResponse.headers,
          config: config as any,
          request: {}
        };
        
        // Return a resolved promise to simulate the async nature of HTTP requests
        return Promise.resolve(response);
      }
      
      console.warn(`%c🎭 [Mock System] No mock data found for ${config.method?.toUpperCase()} ${config.url}`, 'color: #f59e0b; font-weight: bold;');
      console.warn(`%c   Making real API request instead...`, 'color: #f59e0b;');
    }
    
    // Make real request (either in capture mode or when no mock data exists)
    return this.axiosInstance.request<T>(config);
  }

  async get<T = unknown>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.request<T>({ ...config, method: 'get', url });
  }

  async post<T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.request<T>({ ...config, method: 'post', url, data });
  }

  async put<T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.request<T>({ ...config, method: 'put', url, data });
  }

  async patch<T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.request<T>({ ...config, method: 'patch', url, data });
  }

  async delete<T = unknown>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.request<T>({ ...config, method: 'delete', url });
  }
}

const mockAwareHttp = new MockAwareHTTP();

export type Http = typeof mockAwareHttp;
export default mockAwareHttp;
